import { Group, Title, Text, Stack } from '@mantine/core';
import type { ReactNode } from 'react';

/** Props for {@link PageHeader}. */
export interface PageHeaderProps {
  /** Primary heading text for the page. */
  title: string;
  /** Optional subtitle rendered below the title in a dimmed, small font. */
  subtitle?: ReactNode;
  /**
   * Optional right-aligned action area. Typically a `<Group gap='xs'>` of
   * `<Button>` elements (e.g. "New", "Import", "Export").
   */
  actions?: ReactNode;
  /**
   * Title treatment. `'default'` (the default) uses the theme's `h1` —
   * 30px/400 — unchanged for every list-page call site. `'hero'` applies the
   * vendor home dashboard's greeting-scale title (26px/700/-0.02em tracking),
   * taken verbatim from `mtb-design-system/templates/vendor-home/desktop.js`
   * and `record.js` (`M.Title` with an inline `fz`/`fw`/`letterSpacing`
   * override in both — there is no dedicated design token for this
   * treatment). Applied here, opt-in per call site, rather than in the
   * shared generated `@mtb/theme` package, which would silently resize every
   * other page's `<Title order={1}>` too.
   */
  variant?: 'default' | 'hero';
}

/**
 * PageHeader — top-of-page title bar used on every list/dashboard route.
 *
 * Shell: any (desktop / mobile / kiosk).
 *
 * Renders a responsive `<Group justify='space-between' align='flex-end'>` with
 * a left column containing the page title (h1) and an optional dim subtitle,
 * and a right column for action buttons. Both columns are flex-end-aligned so
 * the actions baseline-align with the title when subtitle is absent.
 */
export function PageHeader({ title, subtitle, actions, variant = 'default' }: PageHeaderProps) {
  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" gap="xs">
      <Stack gap={2}>
        <Title
          order={1}
          {...(variant === 'hero'
            ? { fz: 26, fw: 700, style: { letterSpacing: '-0.02em' } }
            : undefined)}
        >
          {title}
        </Title>
        {subtitle != null && (
          <Text size="sm" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Stack>
      {actions != null && <Group gap="xs">{actions}</Group>}
    </Group>
  );
}
